import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  CalendarIcon, 
  UserGroupIcon, 
  UserIcon, 
  DocumentTextIcon,
  ClockIcon,
  BuildingOfficeIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  LockClosedIcon,
  EnvelopeIcon,
  QuestionMarkCircleIcon,
  InformationCircleIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

const Home = () => {
  const { user } = useContext(AuthContext);

  // Définition des variantes d'animation
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const scaleIn = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } }
  };
  
  return (
    <motion.div 
      className="max-w-6xl mx-auto"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      {/* En-tête avec animations */}
      <motion.div className="text-center mb-12" variants={fadeInUp}>
        <motion.div 
          className="flex justify-center mb-6"
          variants={scaleIn}
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <img 
            src="/logo-noir.png" 
            alt="Comédie des Suds" 
            className="h-32 drop-shadow-md"
          />
        </motion.div>
        <motion.div variants={staggerContainer}>
          <motion.h1 
            className="text-3xl font-bold text-primary mb-4 relative inline-block"
            variants={fadeInUp}
          >
            Bienvenue sur la plateforme de gestion des événements
            <motion.span 
              className="absolute -top-4 -right-8 text-gold"
              initial={{ opacity: 0, rotate: -20 }}
              animate={{ opacity: 1, rotate: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              <SparklesIcon className="h-8 w-8" />
            </motion.span>
          </motion.h1>
          <motion.p 
            className="text-lg text-charcoal"
            variants={fadeInUp}
          >
            Un outil complet pour la gestion du calendrier et des ressources du théâtre
          </motion.p>
        </motion.div>
      </motion.div>

      {/* Présentation du théâtre avec animations */}
      <motion.div 
        className="bg-gradient-to-r from-primary/10 to-secondary/10 p-6 rounded-lg shadow-md mb-10 overflow-hidden"
        variants={fadeInUp}
        whileHover={{ boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
      >
        <motion.h2 
          className="text-2xl font-bold text-primary border-b-2 border-gold pb-2 mb-4 inline-block"
          initial={{ width: 0 }}
          animate={{ width: "auto" }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Présentation du théâtre
        </motion.h2>
        <motion.div 
          className="flex flex-col md:flex-row items-center gap-6"
          variants={staggerContainer}
        >
          <motion.div 
            className="flex-1"
            variants={fadeInUp}
          >
            <motion.p 
              className="text-charcoal mb-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Planning-Corporate est un outil qui sert à optimiser la gestion des événements et des ressources pour les organisations culturelles. Notre plateforme permet de :
            </motion.p>
            <motion.ul 
              className="list-disc list-inside space-y-2 text-charcoal"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {[
                "Gérer le calendrier des événements et des répétitions",
                "Coordonner les équipes techniques et artistiques",
                "Suivre l'occupation des salles",
                "Partager des documents importants"
              ].map((item, index) => (
                <motion.li 
                  key={index}
                  variants={fadeInUp}
                  custom={index}
                  transition={{ delay: 0.1 * index }}
                  whileHover={{ x: 5, color: "#800000" }}
                >
                  {item}
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          <motion.div 
            className="flex-1 flex justify-center"
            variants={fadeInUp}
          >
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: <BuildingOfficeIcon className="h-8 w-8 text-primary mr-3" />, text: "Gestion des salles" },
                { icon: <CalendarIcon className="h-8 w-8 text-primary mr-3" />, text: "Programmation" },
                { icon: <UserGroupIcon className="h-8 w-8 text-primary mr-3" />, text: "Équipes" },
                { icon: <DocumentTextIcon className="h-8 w-8 text-primary mr-3" />, text: "Documents" }
              ].map((item, index) => (
                <motion.div 
                  key={index}
                  className="bg-white p-4 rounded-lg shadow-md flex items-center"
                  whileHover={{ scale: 1.05, backgroundColor: "#FFFAF0" }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 * index, duration: 0.4 }}
                >
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    {item.icon}
                  </motion.div>
                  <span className="text-charcoal">{item.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Guide d'utilisation avec animations */}
      <motion.div 
        className="mb-10"
        variants={fadeInUp}
      >
        <motion.h2 
          className="text-2xl font-bold text-primary border-b-2 border-gold pb-2 mb-6 inline-block"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          Guide d'utilisation
        </motion.h2>
        
        {/* Section pour les utilisateurs standards */}
        <motion.div 
          className="mb-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.h3 
            className="text-xl font-semibold text-secondary mb-4"
            variants={fadeInUp}
          >
            Pour les utilisateurs standards
          </motion.h3>
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
            variants={staggerContainer}
          >
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-primary"
              variants={fadeInUp}
              whileHover={{ 
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                borderLeft: "8px solid #800000",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center mb-3">
                <motion.div
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                >
                  <CalendarIcon className="h-6 w-6 text-primary mr-2" />
                </motion.div>
                <h4 className="text-lg font-medium">Consulter le calendrier</h4>
              </div>
              <p className="text-charcoal mb-3">Accédez au calendrier pour visualiser tous les événements programmés, les répétitions et l'occupation des salles.</p>
              <motion.div
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Link to="/calendar" className="text-primary hover:text-accent font-medium flex items-center">
                  Voir le calendrier
                  <motion.svg 
                    className="w-4 h-4 ml-1" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                    initial={{ x: 0 }}
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, repeatType: "loop", duration: 1.5, repeatDelay: 2 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </motion.svg>
                </Link>
              </motion.div>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-primary"
              variants={fadeInUp}
              whileHover={{ 
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                borderLeft: "8px solid #800000",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center mb-3">
                <motion.div
                  whileHover={{ scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <PlusIcon className="h-6 w-6 text-primary mr-2" />
                </motion.div>
                <h4 className="text-lg font-medium">Consulter les événements</h4>
              </div>
              <p className="text-charcoal mb-3">Vous pouvez consulter les détails des événements auxquels vous êtes invité en cliquant dessus dans le calendrier.</p>
              <p className="text-charcoal">
                <motion.span 
                  className="font-medium text-accent"
                  whileHover={{ scale: 1.05 }}
                >
                  Note :
                </motion.span> En tant qu'utilisateur standard, vous ne pouvez pas choisir les participants d'un événement.
              </p>
            </motion.div>
            
            <motion.div 
              className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-primary"
              variants={fadeInUp}
              whileHover={{ 
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                borderLeft: "8px solid #800000",
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center mb-3">
                <motion.div
                  whileHover={{ y: -3 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <UserIcon className="h-6 w-6 text-primary mr-2" />
                </motion.div>
                <h4 className="text-lg font-medium">Gérer votre profil</h4>
              </div>
              <p className="text-charcoal mb-3">Mettez à jour vos informations personnelles, coordonnées et préférences dans votre profil utilisateur.</p>
              <motion.div
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Link to="/profile" className="text-primary hover:text-accent font-medium flex items-center">
                  Accéder à mon profil
                  <motion.svg 
                    className="w-4 h-4 ml-1" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24" 
                    xmlns="http://www.w3.org/2000/svg"
                    initial={{ x: 0 }}
                    animate={{ x: [0, 5, 0] }}
                    transition={{ repeat: Infinity, repeatType: "loop", duration: 1.5, repeatDelay: 2 }}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </motion.svg>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </motion.div>
        
        {/* Section pour les administrateurs avec animations */}
        {user && user.role === 'administrateur' && (
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.h3 
              className="text-xl font-semibold text-secondary mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              Pour les administrateurs
            </motion.h3>
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-6"
              variants={staggerContainer}
            >
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-gold"
                variants={fadeInUp}
                whileHover={{ 
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                  borderLeft: "8px solid #D4AF37",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center mb-3">
                  <motion.div
                    whileHover={{ rotate: [0, -10, 10, -10, 0] }}
                    transition={{ duration: 0.5 }}
                  >
                    <PlusIcon className="h-6 w-6 text-gold mr-2" />
                  </motion.div>
                  <h4 className="text-lg font-medium">Créer et gérer les événements</h4>
                </div>
                <p className="text-charcoal mb-3">En tant qu'administrateur, vous pouvez créer et gérer tous les événements du calendrier.</p>
                <motion.ol 
                  className="list-decimal list-inside text-charcoal space-y-1"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {[
                    "Créez tout type d'événement",
                    "Sélectionnez les participants",
                    "Modifiez ou supprimez des événements existants"
                  ].map((item, index) => (
                    <motion.li 
                      key={index}
                      variants={fadeInUp}
                      custom={index}
                      transition={{ delay: 0.1 * index }}
                      whileHover={{ x: 5, color: "#800000" }}
                    >
                      {item}
                    </motion.li>
                  ))}
                </motion.ol>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                  className="mt-2"
                >
                  <Link to="/calendar" className="text-primary hover:text-accent font-medium flex items-center">
                    Accéder au calendrier
                    <motion.svg 
                      className="w-4 h-4 ml-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, repeatType: "loop", duration: 1.5, repeatDelay: 2 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </motion.svg>
                  </Link>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-gold"
                variants={fadeInUp}
                whileHover={{ 
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                  borderLeft: "8px solid #D4AF37",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center mb-3">
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <DocumentTextIcon className="h-6 w-6 text-gold mr-2" />
                  </motion.div>
                  <h4 className="text-lg font-medium">Gestion des documents</h4>
                </div>
                <p className="text-charcoal mb-3">Vous pouvez ajouter, modifier et supprimer des documents PDF pour tous les événements.</p>
                <motion.ol 
                  className="list-decimal list-inside text-charcoal space-y-1"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {[
                    "Téléchargez des documents PDF",
                    "Associez-les aux événements",
                    "Gérez les autorisations d'accès"
                  ].map((item, index) => (
                    <motion.li 
                      key={index}
                      variants={fadeInUp}
                      custom={index}
                      transition={{ delay: 0.1 * index }}
                      whileHover={{ x: 5, color: "#800000" }}
                    >
                      {item}
                    </motion.li>
                  ))}
                </motion.ol>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-gold"
                variants={fadeInUp}
                whileHover={{ 
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                  borderLeft: "8px solid #D4AF37",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center mb-3">
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <UserGroupIcon className="h-6 w-6 text-gold mr-2" />
                  </motion.div>
                  <h4 className="text-lg font-medium">Gestion des utilisateurs</h4>
                </div>
                <p className="text-charcoal mb-3">En tant qu'administrateur, vous pouvez gérer tous les utilisateurs de la plateforme.</p>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <Link to="/users" className="text-primary hover:text-accent font-medium flex items-center">
                    Gérer les utilisateurs
                    <motion.svg 
                      className="w-4 h-4 ml-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, repeatType: "loop", duration: 1.5, repeatDelay: 2 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </motion.svg>
                  </Link>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-gold"
                variants={fadeInUp}
                whileHover={{ 
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                  borderLeft: "8px solid #D4AF37",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center mb-3">
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                  >
                    <LockClosedIcon className="h-6 w-6 text-gold mr-2" />
                  </motion.div>
                  <h4 className="text-lg font-medium">Utilisateurs bloqués</h4>
                </div>
                <p className="text-charcoal mb-3">Gérez les comptes utilisateurs qui ont été bloqués suite à plusieurs tentatives de connexion échouées.</p>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                >
                  <Link to="/locked-users" className="text-primary hover:text-accent font-medium flex items-center">
                    Voir les utilisateurs bloqués
                    <motion.svg 
                      className="w-4 h-4 ml-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, repeatType: "loop", duration: 1.5, repeatDelay: 2 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </motion.svg>
                  </Link>
                </motion.div>
              </motion.div>
              
              <motion.div 
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-l-gold"
                variants={fadeInUp}
                whileHover={{ 
                  boxShadow: "0 10px 25px rgba(0,0,0,0.1)", 
                  borderLeft: "8px solid #D4AF37",
                  transition: { duration: 0.2 }
                }}
              >
                <div className="flex items-center mb-3">
                  <motion.div
                    whileHover={{ y: -3 }}
                    transition={{ type: "spring", stiffness: 300 }}
                  >
                    <ArrowDownTrayIcon className="h-6 w-6 text-gold mr-2" />
                  </motion.div>
                  <h4 className="text-lg font-medium">Exports et statistiques</h4>
                </div>
                <p className="text-charcoal mb-3">Accédez au tableau de bord pour consulter les statistiques et exporter les données.</p>
                <motion.ol 
                  className="list-decimal list-inside text-charcoal space-y-1"
                  variants={staggerContainer}
                  initial="hidden"
                  animate="visible"
                >
                  {[
                    "Visualisez les statistiques d'occupation des salles",
                    "Analysez la répartition des événements par type",
                    "Exportez les données au format Excel"
                  ].map((item, index) => (
                    <motion.li 
                      key={index}
                      variants={fadeInUp}
                      custom={index}
                      transition={{ delay: 0.1 * index }}
                      whileHover={{ x: 5, color: "#800000" }}
                    >
                      {item}
                    </motion.li>
                  ))}
                </motion.ol>
                <motion.div
                  whileHover={{ x: 5 }}
                  transition={{ type: "spring", stiffness: 400 }}
                  className="mt-2"
                >
                  <Link to="/dashboard" className="text-primary hover:text-accent font-medium flex items-center">
                    Accéder au tableau de bord
                    <motion.svg 
                      className="w-4 h-4 ml-1" 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24" 
                      xmlns="http://www.w3.org/2000/svg"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ repeat: Infinity, repeatType: "loop", duration: 1.5, repeatDelay: 2 }}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </motion.svg>
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </motion.div>
      
      {/* Footer avec animations */}
      <motion.footer 
        className="mt-16 pt-8 border-t border-gray-200"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div 
            className="flex flex-col items-center"
            variants={fadeInUp}
          >
            <motion.img 
              src="/logo-noir.png" 
              alt="Comédie des Suds" 
              className="h-40 mb-4 drop-shadow-md"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            />
            <motion.p 
              className="text-sm text-gray-500 text-center md:text-left"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              La plateforme de gestion des événements du théâtre Comédie des Suds.
            </motion.p>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <motion.h3 
              className="text-lg font-semibold text-primary mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Navigation rapide
            </motion.h3>
            <motion.ul 
              className="space-y-2"
              variants={staggerContainer}
              initial="hidden"
              animate="visible"
            >
              {[
                { to: "/", text: "Accueil" },
                { to: "/calendar", text: "Calendrier" },
                { to: "/profile", text: "Profil" },
                ...(user && user.role === 'administrateur' ? [
                  { to: "/dashboard", text: "Tableau de bord" },
                  { to: "/users", text: "Utilisateurs" }
                ] : [])
              ].map((item, index) => (
                <motion.li 
                  key={index}
                  variants={fadeInUp}
                  custom={index}
                  whileHover={{ x: 5 }}
                  transition={{ delay: 0.1 * index }}
                >
                  <Link to={item.to} className="text-charcoal hover:text-primary transition-colors duration-300">
                    {item.text}
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </motion.div>
          
          <motion.div variants={fadeInUp}>
            <motion.h3 
              className="text-lg font-semibold text-primary mb-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              Contact
            </motion.h3>
            <motion.p 
              className="flex items-center text-charcoal mb-2"
              whileHover={{ x: 5 }}
            >
              <motion.span
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.6 }}
              >
                <EnvelopeIcon className="h-5 w-5 text-primary mr-2" />
              </motion.span>
              <a href="mailto:hotline@mcv-fr.com" className="hover:text-primary transition-colors duration-300">hotline@mcv-fr.com</a>
            </motion.p>
            <motion.p 
              className="text-sm text-gray-500 mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Pour toute question ou problème technique, veuillez contacter notre équipe de maintenance.
            </motion.p>
            <motion.div 
              className="mt-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <motion.p 
                className="text-sm font-medium text-primary mb-2"
                whileHover={{ scale: 1.02 }}
              >
                Informations légales
              </motion.p>
              <motion.p className="text-xs text-gray-500">SIRET: 432896173000 64</motion.p>
              <motion.p className="text-xs text-gray-500">520b avenue du Col de l'Ange 13420 Gémenos</motion.p>
            </motion.div>
          </motion.div>
        </motion.div>
        
        <motion.div 
          className="mt-8 pt-4 border-t border-gray-200 text-center text-sm text-gray-500"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <motion.p
            whileHover={{ scale: 1.05, color: "#800000" }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            © {new Date().getFullYear()} Comédie des Suds. Tous droits réservés.
          </motion.p>
        </motion.div>
      </motion.footer>
    </motion.div>
  );
};

export default Home;